import CameraIcon from "assets/images/CameraIcon";
import {useEffect, useState} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
  common as stringsCommon,
  guestAppointmentInfo as strings,
} from "strings";

import AddInsuranceCardModal from "components/AddInsuranceCardModal";
import Button from "components/Button";
import {DateString} from "components/DateInput";
import Divider from "components/Divider";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import StateInput from "components/StateInput";
import StyledDateInput from "components/StyledDateInput";
import Switch from "components/Switch";
import TextInput from "components/TextInput";

import * as models from "liberty-core/api/models";

import {redirectToAppointments} from "lib/redirect";

import styles from "./GuestAppointmentInfoPage.module.css";

type LocationState = {
  apptCampaigns: models.AppointmentCampaign[];
  guestInfo?: models.AppointmentGuestInfo;
  insuranceInfo?: {frontImage: File; backImage: File};
};

export default function GuestAppointmentInfoPage() {
  const [redirectParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const apptCampaigns = locationState.apptCampaigns;
  const savedGuestInfo = locationState.guestInfo;
  const savedInsuranceInfo = locationState.insuranceInfo;

  const [firstName, setFirstName] = useState(savedGuestInfo?.firstName ?? "");
  const [lastName, setLastName] = useState(savedGuestInfo?.lastName ?? "");
  const [dob, setDOB] = useState<DateString>(
    (savedGuestInfo?.dob as DateString) ?? "",
  );
  const [phone, setPhone] = useState(savedGuestInfo?.phone ?? "");
  const [email, setEmail] = useState(savedGuestInfo?.email ?? "");
  const [street, setStreet] = useState(savedGuestInfo?.street ?? "");
  const [city, setCity] = useState(savedGuestInfo?.city ?? "");
  const [state, setState] = useState(savedGuestInfo?.state ?? "");
  const [zip, setZip] = useState(savedGuestInfo?.zip ?? "");

  const [noInsurance, setNoInsurance] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [cardFront, setCardFront] = useState<File | null>(
    savedInsuranceInfo?.frontImage ?? null,
  );
  const [cardBack, setCardBack] = useState<File | null>(
    savedInsuranceInfo?.backImage ?? null,
  );

  const onSubmit = () => {
    const guestInfo = {
      firstName,
      lastName,
      dob,
      phone,
      email,
      street,
      city,
      state,
      zip,
    };
    const insuranceInfo =
      cardFront && cardBack
        ? {frontImage: cardFront, backImage: cardBack}
        : undefined;
    const campaignId = redirectParams.get("campaignId");
    const optionId = redirectParams.get("optionId");

    redirectToAppointments(
      navigate,
      campaignId,
      optionId,
      guestInfo,
      null,
      apptCampaigns,
      undefined,
      insuranceInfo,
    );
  };

  useEffect(() => {
    if (!apptCampaigns) {
      navigate("/appointments", {replace: true});
    }
  }, [navigate, apptCampaigns]);

  return (
    <div>
      <MobileNavigationHeader backTo="/appointments" alwaysShown />
      <PageTitle>{strings.title}</PageTitle>
      <Divider className={styles.divider} />
      <div className={styles.inputContainer}>
        <TextInput
          label={stringsCommon.firstName}
          type="text"
          autoComplete="first-name"
          value={firstName}
          onChangeText={setFirstName}
        />
        <TextInput
          label={stringsCommon.lastName}
          type="text"
          autoComplete="last-name"
          value={lastName}
          onChangeText={setLastName}
        />
        <StyledDateInput
          label={stringsCommon.dateOfBirth}
          autoComplete="bday"
          value={dob}
          onChangeDate={setDOB}
        />
        <TextInput
          label={strings.phone}
          type="tel"
          autoComplete="phone-number"
          value={phone}
          onChangeText={setPhone}
        />
        <TextInput
          label={stringsCommon.email}
          type="email"
          autoComplete="email"
          value={email}
          onChangeText={setEmail}
        />
        <TextInput
          label={strings.street}
          type="text"
          autoComplete="address"
          value={street}
          onChangeText={setStreet}
        />
        <TextInput
          label={strings.city}
          type="text"
          autoComplete="city"
          value={city}
          onChangeText={setCity}
        />
        <div className={styles.bottomInput}>
          <StateInput
            value={state}
            onChange={setState}
            placeholder={strings.state}
            className={styles.bottomInputItem}
          />
          <TextInput
            label={strings.zip}
            type="text"
            autoComplete="zip"
            value={zip}
            onChangeText={setZip}
            className={styles.bottomInputItem}
          />
        </div>
        <div className={styles.insuranceInstructions}>
          {strings.provideInsurance}
        </div>
        <div className={styles.insuranceSwitch}>
          <Switch checked={noInsurance} onChange={setNoInsurance} />
          <div>{strings.doNotHaveInsurance}</div>
        </div>
      </div>
      <Button
        className={styles.addInsuranceButton}
        variant="unfilled"
        iconLeft={(color: string) => <CameraIcon color={color} />}
        onClick={() => setShowInsuranceModal(true)}
        disabled={noInsurance}
      >
        {cardFront || cardBack ? strings.updateCard : strings.addCard}
      </Button>
      <Button
        onClick={onSubmit}
        variant="filled"
        className={styles.submit}
        disabled={
          !firstName ||
          !lastName ||
          !dob ||
          !phone ||
          !email ||
          !street ||
          !city ||
          !state ||
          !zip ||
          (!noInsurance && (!cardFront || !cardBack))
        }
      >
        {stringsCommon.submit}
      </Button>
      <AddInsuranceCardModal
        visible={showInsuranceModal}
        onClose={() => setShowInsuranceModal(false)}
        onSave={(frontImage: File, backImage: File) => {
          setCardFront(frontImage);
          setCardBack(backImage);
          setShowInsuranceModal(false);
        }}
        frontCard={cardFront ?? undefined}
        backCard={cardBack ?? undefined}
      />
    </div>
  );
}
